import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Stage, Layer, Image as KonvaImage, Rect, Transformer } from 'react-konva';
import useImage from 'use-image';
import Konva from 'konva';
import { useEditorStore, CropState } from '../../store/editorStore';
import { photosApi } from '../../api/photos';
import { overlaysApi } from '../../api/overlays';
import { exportConfigsApi } from '../../api/exportConfigs';
import { FilterControls } from './FilterControls';
import { OverlayPanel } from './OverlayPanel';
import { CropTool } from './CropTool';
import { ExportPanel, ExportPanelHandle } from './ExportPanel';
import { SaveConfigButton } from './SaveConfigButton';
import { Button } from '../UI/Button';
import { Modal } from '../UI/Modal';
import { Spinner } from '../UI/Spinner';
import { PlatformIcon } from '../UI/PlatformIcon';
import { track, EVENTS } from '../../lib/mixpanel';
import toast from 'react-hot-toast';
import { ChevronRight, ArrowLeft, X, Check, Info, Download, RotateCcw, RotateCw, Star } from 'lucide-react';
import { EditorStep, ExportConfig, OVERLAY_SIZE_FACTORS } from '../../types';
import { computeOverlayPlacement } from '../../lib/overlayPlacement';
import styles from './EditorModal.module.css';

const STEPS: { key: EditorStep; label: string; number: number }[] = [
  { key: 'preset', label: 'Apply Preset', number: 1 },
  { key: 'adjust', label: 'Adjust', number: 2 },
  { key: 'crop', label: 'Crop', number: 3 },
  { key: 'overlay', label: 'Overlay', number: 4 },
  { key: 'export', label: 'Export', number: 5 },
];

const CANVAS_MAX_WIDTH = 700;
const CANVAS_MAX_HEIGHT = 500;

interface OverlayPreviewProps {
  overlayUrl: string;
  canvasWidth: number;
  canvasHeight: number;
  anchor: string;
  sizeFactor: number;
  opacity: number;
}

const OverlayPreview: React.FC<OverlayPreviewProps> = ({
  overlayUrl,
  canvasWidth,
  canvasHeight,
  anchor,
  sizeFactor,
  opacity,
}) => {
  const [image] = useImage(overlayUrl, 'anonymous');
  if (!image) return null;

  const { x, y, scale } = computeOverlayPlacement(
    anchor,
    sizeFactor,
    canvasWidth,
    canvasHeight,
    image.width,
    image.height,
  );

  return (
    <KonvaImage
      image={image}
      x={x}
      y={y}
      scaleX={scale}
      scaleY={scale}
      opacity={opacity}
    />
  );
};

interface CropHandlesProps {
  crop: CropState;
  canvasWidth: number;
  canvasHeight: number;
  onCropChange: (crop: Partial<CropState>) => void;
}

// Inset margin so crop handles stay within the visible canvas.
// Must be >= the Transformer anchorSize/2 (14/2 = 7) so resize handles
// near the image edge aren't clipped by the canvas bounds.
const CROP_HANDLE_MARGIN = 10;

const CropHandles: React.FC<CropHandlesProps> = ({ crop, canvasWidth, canvasHeight, onCropChange }) => {
  const cropRef = useRef<Konva.Rect>(null);
  const trRef = useRef<Konva.Transformer>(null);

  useEffect(() => {
    if (trRef.current && cropRef.current) {
      trRef.current.nodes([cropRef.current]);
      trRef.current.getLayer()?.batchDraw();
    }
  }, [crop.enabled]);

  if (!crop.enabled) return null;

  // Clamp values so handles (which extend CROP_HANDLE_MARGIN px outward) stay within the stage
  const m = CROP_HANDLE_MARGIN;
  const clampedX = Math.max(m, Math.min(crop.x, canvasWidth - crop.width - m));
  const clampedY = Math.max(m, Math.min(crop.y, canvasHeight - crop.height - m));

  return (
    <>
      {/* Dimming overlay — 4 rects around the crop area */}
      <Rect x={0} y={0} width={canvasWidth} height={clampedY} fill="rgba(0,0,0,0.5)" listening={false} />
      <Rect x={0} y={clampedY} width={clampedX} height={crop.height} fill="rgba(0,0,0,0.5)" listening={false} />
      <Rect x={clampedX + crop.width} y={clampedY} width={canvasWidth - clampedX - crop.width} height={crop.height} fill="rgba(0,0,0,0.5)" listening={false} />
      <Rect x={0} y={clampedY + crop.height} width={canvasWidth} height={canvasHeight - clampedY - crop.height} fill="rgba(0,0,0,0.5)" listening={false} />

      {/* Crop rect */}
      <Rect
        ref={cropRef}
        x={clampedX}
        y={clampedY}
        width={crop.width}
        height={crop.height}
        stroke="#ef4444"
        strokeWidth={2}
        dash={[6, 3]}
        draggable
        dragBoundFunc={(pos) => {
          const nx = Math.max(m, Math.min(pos.x, canvasWidth - crop.width - m));
          const ny = Math.max(m, Math.min(pos.y, canvasHeight - crop.height - m));
          return { x: nx, y: ny };
        }}
        onDragEnd={(e) => {
          onCropChange({ x: e.target.x(), y: e.target.y() });
        }}
        onTransformEnd={() => {
          const node = cropRef.current;
          if (!node) return;
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();
          let nw = Math.max(20, node.width() * scaleX);
          let nh = Math.max(20, node.height() * scaleY);
          let nx = node.x();
          let ny = node.y();
          // Clamp so handles stay within canvas
          nx = Math.max(m, Math.min(nx, canvasWidth - nw - m));
          ny = Math.max(m, Math.min(ny, canvasHeight - nh - m));
          nw = Math.min(nw, canvasWidth - nx - m);
          nh = Math.min(nh, canvasHeight - ny - m);
          node.scaleX(1);
          node.scaleY(1);
          node.x(nx);
          node.y(ny);
          node.width(nw);
          node.height(nh);
          onCropChange({ x: nx, y: ny, width: nw, height: nh });
        }}
      />
      <Transformer
        ref={trRef}
        rotateEnabled={false}
        keepRatio={false}
        enabledAnchors={[
          'top-left', 'top-center', 'top-right',
          'middle-left', 'middle-right',
          'bottom-left', 'bottom-center', 'bottom-right',
        ]}
        boundBoxFunc={(oldBox, newBox) => {
          if (newBox.width < 20 || newBox.height < 20) return oldBox;
          // Keep the box within canvas bounds (with margin for handles)
          const clamped = { ...newBox };
          if (clamped.x < m) { clamped.width -= m - clamped.x; clamped.x = m; }
          if (clamped.y < m) { clamped.height -= m - clamped.y; clamped.y = m; }
          if (clamped.x + clamped.width > canvasWidth - m) clamped.width = canvasWidth - m - clamped.x;
          if (clamped.y + clamped.height > canvasHeight - m) clamped.height = canvasHeight - m - clamped.y;
          if (clamped.width < 20 || clamped.height < 20) return oldBox;
          return clamped;
        }}
        borderStroke="#ef4444"
        anchorStroke="#ef4444"
        anchorFill="#ef4444"
        anchorSize={14}
        anchorCornerRadius={3}
      />
    </>
  );
};

/** Visual bar showing a bipolar filter value (positive = green, negative = amber) */
const PresetFilterBar: React.FC<{ label: string; value: number; min: number; max: number; decimals: number }> = ({
  label, value, min, max, decimals,
}) => {
  const range = max - min;
  const pct = ((value - min) / range) * 100;
  const centerPct = ((-min) / range) * 100;
  const fillLeft = Math.min(pct, centerPct);
  const fillWidth = Math.abs(pct - centerPct);
  const isPositive = value >= 0;
  const isZero = value === 0;

  return (
    <div>
      <div className={styles['filter-bar-header']}>
        <span className={styles['filter-bar-label']}>{label}</span>
        <span className={[styles['filter-bar-value'], isZero ? styles['filter-bar-value-zero'] : isPositive ? styles['filter-bar-value-positive'] : styles['filter-bar-value-negative']].join(' ')}>
          {isPositive && !isZero ? '+' : ''}{value.toFixed(decimals)}
        </span>
      </div>
      <div className={styles['filter-bar-track']}>
        {/* Fill from center to value */}
        <div
          className={[styles['filter-bar-fill'], styles['filter-fill'], isPositive ? styles['filter-bar-fill-positive'] : styles['filter-bar-fill-negative'], isZero ? styles['filter-bar-fill-hidden'] : ''].filter(Boolean).join(' ')}
          style={{ '--fill-left': `${fillLeft}%`, '--fill-width': `${fillWidth}%` } as React.CSSProperties}
        />
        {/* Center zero marker */}
        <div className={[styles['filter-bar-center-marker'], styles['filter-center-line']].join(' ')} style={{ '--center-pct': `${centerPct}%` } as React.CSSProperties} />
        {/* Thumb */}
        <div
          className={[styles['filter-bar-thumb'], styles['filter-thumb']].join(' ')}
          style={{ '--thumb-pct': `${pct}%` } as React.CSSProperties}
        />
      </div>
    </div>
  );
};

export const EditorModal: React.FC = () => {
  const {
    isOpen,
    photo,
    overlays,
    step,
    filters,
    crop,
    overlayConfig,
    brandingOverlayConfig,
    rotation,
    setStep,
    nextStep,
    setCrop,
    closeEditor,
    rotateLeft,
    rotateRight,
    setPhotoHighlighted,
  } = useEditorStore();

  const [highlightPromptOpen, setHighlightPromptOpen] = useState(false);
  const [savingHighlight, setSavingHighlight] = useState(false);
  // Only offer the highlight prompt once per editor session.
  const highlightPromptedRef = useRef(false);

  // Fired by ExportPanel after ANY successful export (single format or ZIP),
  // regardless of how the editor was opened (tile or photo viewer). Reads the
  // photo fresh from the store to avoid a stale closure.
  const handleExported = () => {
    const current = useEditorStore.getState().photo;
    if (highlightPromptedRef.current || !current || current.isHighlight) return;
    highlightPromptedRef.current = true;
    setHighlightPromptOpen(true);
  };

  const handleConfirmHighlight = async () => {
    if (!photo) return;
    setSavingHighlight(true);
    setPhotoHighlighted(true); // optimistic (updates store + gallery)
    try {
      await photosApi.batchSetHighlight(photo.eventId, [photo._id], true);
      toast.success('Added to highlights — it will show in the album');
    } catch {
      setPhotoHighlighted(false); // revert
      toast.error('Could not highlight the photo');
    } finally {
      setSavingHighlight(false);
      setHighlightPromptOpen(false);
      handleClose(); // close the editor after the choice is made
    }
  };

  // Dismiss the prompt (cancel / backdrop / escape) and close the editor too.
  const dismissHighlightPrompt = () => {
    setHighlightPromptOpen(false);
    handleClose();
  };

  const stageRef = useRef<Konva.Stage>(null);
  const imageRef = useRef<Konva.Image>(null);
  const exportPanelRef = useRef<ExportPanelHandle>(null);
  const editorOpenedAt = useRef<number>(Date.now());

  const { selectedFormats } = useEditorStore();

  // Preset step state
  const [presets, setPresets] = useState<ExportConfig[]>([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [presetsError, setPresetsError] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [infoPresetId, setInfoPresetId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      editorOpenedAt.current = Date.now();
      highlightPromptedRef.current = false;
    }
  }, [isOpen]);

  const loadPresets = (eventId: string) => {
    setPresetsLoading(true);
    setPresetsError(false);
    exportConfigsApi.list(eventId)
      .then((data) => { setPresets(data); })
      .catch(() => { setPresets([]); setPresetsError(true); })
      .finally(() => setPresetsLoading(false));
  };

  useEffect(() => {
    if (!isOpen || !photo || step !== 'preset') return;
    setSelectedPresetId(null);
    loadPresets(photo.eventId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, photo?.eventId, step]);

  const photoUrl = photo ? photosApi.originalUrl(photo._id) : '';
  const [image, imageStatus] = useImage(photoUrl, 'anonymous');

  const [baseCanvasSize, setBaseCanvasSize] = useState({ width: CANVAS_MAX_WIDTH, height: CANVAS_MAX_HEIGHT });
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({ width: CANVAS_MAX_WIDTH, height: CANVAS_MAX_HEIGHT });

  // Track the available display area so we can fit the image into it
  useEffect(() => {
    if (!isOpen) return;
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cr = entry.contentRect;
        setContainerSize({ width: Math.max(100, Math.floor(cr.width)), height: Math.max(100, Math.floor(cr.height)) });
      }
    });
    ro.observe(el);
    // Initialize immediately
    const rect = el.getBoundingClientRect();
    setContainerSize({ width: Math.max(100, Math.floor(rect.width)), height: Math.max(100, Math.floor(rect.height)) });
    return () => ro.disconnect();
  }, [isOpen]);

  // Compute canvas size based on view mode.
  useEffect(() => {
    if (!image) return;
    // For 90/270° rotation the effective dimensions swap.
    const isRotated90or270 = rotation === 90 || rotation === 270;
    const effectiveW = isRotated90or270 ? image.height : image.width;
    const effectiveH = isRotated90or270 ? image.width : image.height;
    const maxW = Math.max(100, containerSize.width - 24);
    const maxH = Math.max(100, containerSize.height - 24);
    const aspectRatio = effectiveW / effectiveH;
    let w = Math.min(maxW, effectiveW);
    let h = Math.round(w / aspectRatio);
    if (h > maxH) {
      h = Math.min(maxH, effectiveH);
      w = Math.round(h * aspectRatio);
    }
    const next = { width: Math.round(w), height: Math.round(h) };
    const prev = baseCanvasSize;
    if (prev.width !== next.width || prev.height !== next.height) {
      const scaleX = next.width / prev.width;
      const scaleY = next.height / prev.height;
      if (isFinite(scaleX) && isFinite(scaleY) && prev.width > 0 && prev.height > 0 && crop.enabled && crop.width > 0 && crop.height > 0) {
        setCrop({
          x: Math.round(crop.x * scaleX),
          y: Math.round(crop.y * scaleY),
          width: Math.round(crop.width * scaleX),
          height: Math.round(crop.height * scaleY),
        });
      }
      setBaseCanvasSize(next);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image, containerSize.width, containerSize.height, rotation]);

  const showCroppedPreview = crop.enabled && step !== 'crop' && crop.width > 0 && crop.height > 0;

  const displayCanvasSize = useMemo(() => {
    const maxW = Math.max(100, containerSize.width - 24);
    const maxH = Math.max(100, containerSize.height - 24);
    const sourceWidth = showCroppedPreview ? crop.width : baseCanvasSize.width;
    const sourceHeight = showCroppedPreview ? crop.height : baseCanvasSize.height;
    const aspectRatio = sourceWidth / sourceHeight;

    let width = Math.min(maxW, sourceWidth);
    let height = Math.round(width / aspectRatio);

    if (height > maxH) {
      height = Math.min(maxH, sourceHeight);
      width = Math.round(height * aspectRatio);
    }

    return { width: Math.round(width), height: Math.round(height) };
  }, [baseCanvasSize.height, baseCanvasSize.width, containerSize.height, containerSize.width, crop.height, crop.width, showCroppedPreview]);

  const imageCrop = useMemo(() => {
    if (!image || !showCroppedPreview) return undefined;

    const scaleX = image.width / baseCanvasSize.width;
    const scaleY = image.height / baseCanvasSize.height;

    return {
      x: crop.x * scaleX,
      y: crop.y * scaleY,
      width: crop.width * scaleX,
      height: crop.height * scaleY,
    };
  }, [baseCanvasSize.height, baseCanvasSize.width, crop.height, crop.width, crop.x, crop.y, image, showCroppedPreview]);

  // Apply filters
  useEffect(() => {
    const node = imageRef.current;
    if (!node || !image) return;

    const activeFilters: Array<(imageData: ImageData) => void> = [];

    if (filters.grayscale) activeFilters.push(Konva.Filters.Grayscale);
    if (filters.brightness !== 0) activeFilters.push(Konva.Filters.Brighten);
    if (filters.contrast !== 0) activeFilters.push(Konva.Filters.Contrast);
    if (filters.saturation !== 0) activeFilters.push(Konva.Filters.HSL);

    node.filters(activeFilters);
    node.brightness(filters.brightness);
    node.contrast(filters.contrast);
    node.saturation(filters.saturation);
    node.clearCache();
    node.cache();
    node.getLayer()?.batchDraw();
  }, [displayCanvasSize.height, displayCanvasSize.width, filters, image, imageCrop]);

  const activeOverlayData = overlayConfig
    ? overlays.find((o) => o._id === overlayConfig.overlayId)
    : null;

  const activeBrandingOverlayData = brandingOverlayConfig
    ? overlays.find((o) => o._id === brandingOverlayConfig.overlayId)
    : null;

  const currentStepIndex = STEPS.findIndex((s) => s.key === step);
  const isLastStep = currentStepIndex === STEPS.length - 1;

  // Compute KonvaImage position/size/rotation so the image fills the stage correctly.
  // For 90°/270° the image dimensions are swapped and centered with an offset anchor.
  const imageKonvaProps = useMemo(() => {
    const cW = displayCanvasSize.width;
    const cH = displayCanvasSize.height;
    if (rotation === 0) return { x: 0, y: 0, offsetX: 0, offsetY: 0, width: cW, height: cH, rotation: 0 as const };
    const isOdd = rotation === 90 || rotation === 270;
    const imgW = isOdd ? cH : cW;
    const imgH = isOdd ? cW : cH;
    return { x: cW / 2, y: cH / 2, offsetX: imgW / 2, offsetY: imgH / 2, width: imgW, height: imgH, rotation };
  }, [displayCanvasSize.width, displayCanvasSize.height, rotation]);

  const handleClose = () => {
    const timeSpent = Math.round((Date.now() - editorOpenedAt.current) / 1000);
    track(EVENTS.EDITOR_CLOSED, {
      event_id: photo?.eventId,
      photo_id: photo?._id,
      step_reached: step,
      time_spent_seconds: timeSpent,
    });
    closeEditor();
  };

  const handleStepChange = (newStep: EditorStep) => {
    track(EVENTS.EDITOR_STEP_CHANGED, { from_step: step, to_step: newStep });
    setStep(newStep);
  };

  const handleNextStep = () => {
    const idx = STEPS.findIndex((s) => s.key === step);
    if (idx < STEPS.length - 1) {
      track(EVENTS.EDITOR_STEP_CHANGED, { from_step: step, to_step: STEPS[idx + 1].key });
    }
    nextStep();
  };

  const handleApplyPresetAndExport = () => {
    const preset = presets.find((p) => p._id === selectedPresetId);
    if (preset) {
      track(EVENTS.PRESET_APPLIED, {
        event_id: photo?.eventId,
        preset_name: preset.name,
        format_count: preset.selectedFormats.length,
        platforms: preset.selectedPlatforms,
      });
      useEditorStore.getState().loadConfig({
        filters: preset.filters,
        overlay: preset.overlay,
        selectedPlatforms: preset.selectedPlatforms,
        selectedFormats: preset.selectedFormats,
      });
    }
    track(EVENTS.EDITOR_STEP_CHANGED, { from_step: 'preset', to_step: 'export' });
    useEditorStore.getState().setStep('export');
  };

  if (!isOpen || !photo) return null;

  const currentStepLabel = STEPS[currentStepIndex]?.label ?? '';

  return (
    <div className={styles['modal-backdrop']}>
      <div className={styles['modal-shell']}>
      {/* Top bar — back + stepper + close */}
      <div className={styles['top-bar']}>
        {/* Mobile: back arrow */}
        <button
          onClick={handleClose}
          className={styles['mobile-back-btn']}
          aria-label="Close editor"
        >
          <ArrowLeft className={styles['icon-5']} />
        </button>

        <div className={styles['stepper-wrapper']}>
          <div className={styles['stepper-scroll']}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s.key}>
              <button
                onClick={() => handleStepChange(s.key)}
                className={[styles['step-btn'], step === s.key ? styles['step-btn-active'] : i < currentStepIndex ? styles['step-btn-completed'] : styles['step-btn-default']].join(' ')}
              >
                <span
                  className={[styles['step-badge'], step === s.key ? styles['step-badge-active'] : i < currentStepIndex ? styles['step-badge-completed'] : styles['step-badge-default']].join(' ')}
                >
                  {i < currentStepIndex ? (
                    <Check className={styles['icon-3']} />
                  ) : (
                    s.number
                  )}
                </span>
                {/* Labels hidden on mobile, visible on sm+ */}
                <span className={styles['step-label']}>{s.label}</span>
              </button>
              {i < STEPS.length - 1 && (
                <ChevronRight className={[styles['step-chevron'], styles['step-chevron-sm']].join(' ')} />
              )}
            </React.Fragment>
          ))}
          </div>
        </div>

        {/* Desktop: X close button */}
        <div className={styles['desktop-close-wrapper']}>
          <button
            onClick={handleClose}
            className={styles['desktop-close-btn']}
          >
            <X className={styles['icon-5']} />
          </button>
        </div>
        {/* Mobile: X close button (compact) */}
        <button
          onClick={handleClose}
          className={styles['mobile-close-btn']}
          aria-label="Close editor"
        >
          <X className={styles['icon-4-5']} />
        </button>
      </div>

      {/* Mobile: Step progress bar */}
      <div className={styles['mobile-progress-track']}>
        <div
          className={[styles['mobile-progress-fill'], styles['progress-bar']].join(' ')}
          style={{ '--progress-width': `${((currentStepIndex + 1) / STEPS.length) * 100}%` } as React.CSSProperties}
        />
      </div>

      {/* Main content — stacked on mobile, side-by-side on desktop */}
      <div className={styles['main-content']}>
        {/* Canvas area */}
        <div className={styles['canvas-area']}>
          {imageStatus === 'loading' ? (
            <div className={styles['canvas-status']}>
              <Spinner size="lg" />
            </div>
          ) : imageStatus === 'failed' ? (
            <div className={styles['canvas-status']}>
              <div className={styles['error-text']}>Failed to load image</div>
            </div>
          ) : (
            <div ref={containerRef} className={styles['canvas-container']}>
              {/* Rotate controls — bottom-right on mobile, top-right on desktop */}
              <div className={styles['rotate-controls']}>
                <button
                  onClick={rotateLeft}
                  className={styles['rotate-btn']}
                  title="Rotate left"
                >
                  <RotateCcw className={styles['icon-3-5']} />
                </button>
                <button
                  onClick={rotateRight}
                  className={styles['rotate-btn']}
                  title="Rotate right"
                >
                  <RotateCw className={styles['icon-3-5']} />
                </button>
              </div>
              <Stage
                ref={stageRef}
                width={displayCanvasSize.width}
                height={displayCanvasSize.height}
                className={styles['stage-canvas']}
              >
                <Layer>
                  {image && (
                    <KonvaImage
                      ref={imageRef}
                      image={image}
                      {...imageKonvaProps}
                      crop={imageCrop}
                    />
                  )}
                  {activeBrandingOverlayData && brandingOverlayConfig && (
                    <OverlayPreview
                      overlayUrl={overlaysApi.fileUrl(activeBrandingOverlayData._id)}
                      canvasWidth={displayCanvasSize.width}
                      canvasHeight={displayCanvasSize.height}
                      anchor={brandingOverlayConfig.anchor}
                      sizeFactor={OVERLAY_SIZE_FACTORS[brandingOverlayConfig.size]}
                      opacity={brandingOverlayConfig.opacity}
                    />
                  )}
                  {activeOverlayData && overlayConfig && (
                    <OverlayPreview
                      overlayUrl={overlaysApi.fileUrl(activeOverlayData._id)}
                      canvasWidth={displayCanvasSize.width}
                      canvasHeight={displayCanvasSize.height}
                      anchor={overlayConfig.anchor}
                      sizeFactor={OVERLAY_SIZE_FACTORS[overlayConfig.size]}
                      opacity={overlayConfig.opacity}
                    />
                  )}
                  {step === 'crop' && (
                    <CropHandles
                      crop={crop}
                      canvasWidth={baseCanvasSize.width}
                      canvasHeight={baseCanvasSize.height}
                      onCropChange={setCrop}
                    />
                  )}
                </Layer>
              </Stage>
            </div>
          )}
        </div>

        {/* Right / bottom panel */}
        <div className={styles['side-panel']}>
          <div className={styles['panel-header']}>
            {/* Mobile: show current step label as heading */}
            <p className={styles['mobile-step-heading']}>{currentStepLabel}</p>
            <p className={styles['desktop-filename']}>{photo.originalFilename}</p>
            <p className={styles['mobile-filename']}>{photo.originalFilename}</p>
          </div>

          <div className={styles['panel-body']}>
            {step === 'preset' && (
              <div className={styles['preset-step']}>
                <p className={styles['preset-description']}>
                  Choose a saved preset to apply its adjustments, overlay, and export formats — then export in one click.
                </p>
                {presetsLoading ? (
                  <div className={styles['preset-loading']}>
                    <Spinner size="sm" /> Loading presets…
                  </div>
                ) : presetsError ? (
                  <div className={styles['preset-empty']}>
                    <p>Failed to load presets.</p>
                    <button
                      onClick={() => photo && loadPresets(photo.eventId)}
                      className={styles['preset-retry-btn']}
                    >
                      Retry
                    </button>
                  </div>
                ) : presets.length === 0 ? (
                  <div className={styles['preset-empty']}>
                    <p>No presets saved yet.</p>
                    <p className={styles['preset-empty-sub']}>Save a preset from the Export step to reuse settings.</p>
                  </div>
                ) : (
                  <div className={styles['preset-list']}>
                    {presets.map((preset) => {
                      const isInfoOpen = infoPresetId === preset._id;
                      return (
                        <div key={preset._id} className={styles['preset-item']}>
                          <div className={styles['preset-item-row']}>
                            <button
                              onClick={() => setSelectedPresetId(preset._id === selectedPresetId ? null : preset._id ?? null)}
                              className={[styles['preset-btn'], selectedPresetId === preset._id ? styles['preset-btn-selected'] : ''].filter(Boolean).join(' ')}
                            >
                              <div className={styles['preset-name']}>{preset.name}</div>
                              <div className={styles['preset-meta']}>
                                {preset.selectedFormats.length} format{preset.selectedFormats.length !== 1 ? 's' : ''} · {preset.selectedPlatforms.join(', ')}
                              </div>
                            </button>
                            {/* Info icon */}
                            <button
                              onClick={() => setInfoPresetId(isInfoOpen ? null : preset._id ?? null)}
                              className={[styles['preset-info-btn'], isInfoOpen ? styles['preset-info-btn-open'] : ''].filter(Boolean).join(' ')}
                              title="Preset details"
                            >
                              <Info className={styles['icon-3-5']} />
                            </button>
                          </div>

                          {/* Expandable preset detail panel */}
                          {isInfoOpen && (
                            <div className={styles['preset-detail']}>
                              {/* Filter adjustments */}
                              <div>
                                <p className={styles['preset-detail-heading']}>Adjustments</p>
                                <div className={styles['preset-adjustments-list']}>
                                  <PresetFilterBar label="Brightness" value={preset.filters.brightness} min={-1} max={1} decimals={2} />
                                  <PresetFilterBar label="Contrast" value={preset.filters.contrast} min={-100} max={100} decimals={0} />
                                  <PresetFilterBar label="Saturation" value={preset.filters.saturation} min={-2} max={2} decimals={2} />
                                  <div className={styles['bw-row']}>
                                    <span className={styles['bw-label']}>Black &amp; White</span>
                                    <span className={[styles['bw-badge'], preset.filters.grayscale ? styles['bw-badge-on'] : styles['bw-badge-off']].join(' ')}>
                                      {preset.filters.grayscale ? 'On' : 'Off'}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Overlay */}
                              <div>
                                <p className={styles['preset-detail-heading-sm']}>Overlay</p>
                                <span className={styles['overlay-info-text']}>
                                  {preset.overlay ? `Applied · opacity ${Math.round((preset.overlay.opacity ?? 1) * 100)}%` : 'None'}
                                </span>
                              </div>

                              {/* Platforms */}
                              {preset.selectedPlatforms.length > 0 && (
                                <div>
                                  <p className={styles['preset-detail-heading-sm']}>Platforms</p>
                                  <div className={styles['platforms-row']}>
                                    {preset.selectedPlatforms.map((p) => (
                                      <div key={p} className={styles['platform-item']}>
                                        <PlatformIcon platform={p} className={styles['icon-3']} />
                                        <span>{p}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Formats */}
                              {preset.selectedFormats.length > 0 && (
                                <div>
                                  <p className={styles['preset-detail-heading-sm']}>
                                    Formats ({preset.selectedFormats.length})
                                  </p>
                                  <div className={styles['formats-row']}>
                                    {preset.selectedFormats.map((f) => (
                                      <span key={f} className={styles['format-tag']}>
                                        {f}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {step === 'adjust' && <FilterControls />}
            {step === 'crop' && (
              <CropTool
                imageWidth={baseCanvasSize.width}
                imageHeight={baseCanvasSize.height}
              />
            )}
            {step === 'overlay' && <OverlayPanel canvasWidth={displayCanvasSize.width} canvasHeight={displayCanvasSize.height} />}
            {step === 'export' && (
              <ExportPanel
                ref={exportPanelRef}
                photoFilename={photo.originalFilename}
                photoId={photo._id}
                canvasWidth={baseCanvasSize.width}
                onExported={handleExported}
              />
            )}
          </div>

          {/* Bottom nav */}
          <div className={styles['bottom-nav']}>
            {step === 'preset' ? (
              <>
                <div className={styles['nav-btn']}>
                  <Button variant="secondary" size="md" onClick={() => { track(EVENTS.PRESET_SKIPPED, { event_id: photo?.eventId }); handleNextStep(); }}>
                    Skip
                  </Button>
                </div>
                <div className={styles['nav-btn']}>
                  <Button
                    variant="primary"
                    size="md"
                    onClick={handleApplyPresetAndExport}
                    disabled={!selectedPresetId}
                  >
                    Apply &amp; Export
                  </Button>
                </div>
              </>
            ) : step === 'export' ? (
              <div className={styles['export-col']}>
                <SaveConfigButton eventId={photo.eventId} />
                <div className={styles['nav-btn-full']}>
                  <Button
                    variant="primary"
                    size="md"
                    onClick={() => exportPanelRef.current?.exportAll()}
                    disabled={selectedFormats.length === 0}
                  >
                    <Download className={styles['icon-4']} />
                    Export {selectedFormats.length} File{selectedFormats.length !== 1 ? 's' : ''} as ZIP
                  </Button>
                </div>
              </div>
            ) : (
              !isLastStep && (
                <div className={styles['nav-btn-full']}>
                  <Button variant="primary" size="md" onClick={handleNextStep}>
                    Next
                    <ChevronRight className={styles['icon-4']} />
                  </Button>
                </div>
              )
            )}
          </div>
        </div>
      </div>
      </div>

      {/* Post-export prompt — offer to feature this photo as a highlight */}
      <Modal
        isOpen={highlightPromptOpen}
        onClose={dismissHighlightPrompt}
        title="Add to highlights?"
        size="sm"
      >
        <div className={styles['highlight-prompt']}>
          <div className={styles['highlight-prompt-icon']}>
            <Star className={styles['icon-4']} fill="currentColor" strokeWidth={1.5} />
          </div>
          <p className={styles['highlight-prompt-text']}>
            Mark this photo as a highlight so it shows up in the album for guests —
            even those viewing only their own photos.
          </p>
          <div className={styles['highlight-prompt-actions']}>
            <Button variant="secondary" size="md" onClick={dismissHighlightPrompt} disabled={savingHighlight}>
              Not now
            </Button>
            <Button variant="primary" size="md" onClick={handleConfirmHighlight} disabled={savingHighlight}>
              <Star className={styles['icon-4']} fill="currentColor" strokeWidth={1.5} />
              {savingHighlight ? 'Adding…' : 'Make highlight'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
